import { z } from 'zod';

/**
 * Mandanten-Dokumente in der fachlichen Struktur
 * `LegalClient → Dokumenttyp → Version → Dateien`.
 *
 * Ein Dokumenttyp ist das Dokument (z. B. „Personalausweis"), eine Version ein
 * bestimmter Stand davon, und eine Version besteht aus einer oder mehreren
 * Dateien (z. B. Vorder- und Rückseite).
 */

/** Eine Datei innerhalb einer Version. */
export const legalClientDocumentFileSchema = z.object({
  idDocument: z.number(),
  FileName: z.string(),
  idMIMEType: z.number(),
  /** Größe in Byte; beim Hochladen noch nicht bekannt. */
  FileSize: z.number().nullable().optional(),
  /** Bezeichnung des Teils, z. B. „Vorderseite". */
  Label: z.string().nullable().optional(),
  Position: z.number().nullable().optional(),
  /** Nur beim Hochladen gesetzt (Base64); wird nie mit gelesen. */
  Data: z.string().optional(),
});

/** Ein Stand eines Dokumenttyps. */
export const legalClientDocumentVersionSchema = z.object({
  /** `null` bei Bestandsdaten ohne Versionsbezug, negativ bei neuen Versionen. */
  idLegalClientDocumentVersion: z.number().nullable(),
  idLegalClientDocumentType: z.number(),
  VersionNumber: z.number(),
  Created_at: z.string().nullable().optional(),
  idUser: z.number().nullable().optional(),
  UserFirstName: z.string().nullable().optional(),
  UserLastName: z.string().nullable().optional(),
  /** Gültigkeitsinformation, z. B. Ablaufdatum eines Ausweises. */
  ValidUntil: z.string().nullable().optional(),
  Note: z.string().nullable().optional(),
  /** Vom Backend gesetzt: neueste Version dieses Typs. */
  isCurrent: z.boolean().optional(),
  FileArray: z.array(legalClientDocumentFileSchema),
});

/** Ein Dokumenttyp mit seiner Versionshistorie. */
export const legalClientDocumentTypeEntrySchema = z.object({
  idLegalClientDocumentType: z.number(),
  LegalClientDocumentType: z.string(),
  Position: z.number().nullable().optional(),
  /**
   * Ist der Typ für die Mandantenart des Mandanten vorgesehen? `false` bei
   * Dokumenten, die noch aus einer anderen Mandantenart stammen.
   */
  isAvailableForLegalClientType: z.boolean().optional(),
  VersionArray: z.array(legalClientDocumentVersionSchema),
});

export type ILegalClientDocumentFile = z.infer<
  typeof legalClientDocumentFileSchema
>;
export type ILegalClientDocumentVersion = z.infer<
  typeof legalClientDocumentVersionSchema
>;
export type ILegalClientDocumentTypeEntry = z.infer<
  typeof legalClientDocumentTypeEntrySchema
>;

/** Aktuelle Version eines Typs — sonst `undefined`, wenn nichts hinterlegt ist. */
export const getCurrentVersion = (
  typeEntry: ILegalClientDocumentTypeEntry
): ILegalClientDocumentVersion | undefined =>
  typeEntry.VersionArray.find(version => version.isCurrent === true) ??
  typeEntry.VersionArray[0];

/** Nächste freie temporäre (negative) ID für eine neue Version. */
export const getNextTempVersionId = (
  typeEntryArray: ILegalClientDocumentTypeEntry[]
): number => {
  const idArray = typeEntryArray.flatMap(typeEntry =>
    typeEntry.VersionArray.map(
      version => version.idLegalClientDocumentVersion ?? 0
    )
  );
  const minId = idArray.length > 0 ? Math.min(...idArray) : 0;

  return minId >= 0 ? -1 : minId - 1;
};

/** Dateigröße lesbar darstellen. */
export const formatFileSize = (fileSize: number | null | undefined): string => {
  if (fileSize === null || fileSize === undefined) {
    return '';
  }

  if (fileSize < 1024) {
    return `${fileSize} B`;
  }

  if (fileSize < 1024 * 1024) {
    return `${(fileSize / 1024).toFixed(0)} KB`;
  }

  return `${(fileSize / (1024 * 1024)).toFixed(1)} MB`;
};
